import type { DrawerProps } from '@mui/material';
import { css, Drawer, drawerClasses, styled } from '@mui/material';
import { type PropsWithChildren, type ReactNode } from 'react';
import useMeasure from 'react-use-measure';

import { FrontModal, FrontModalHeader } from '../modal';
import { FrontModalCloseButton } from '../modal/components/FrontModalCloseButton';
import { type DrawerWidth, drawerWidths } from '../theme/drawerWidths';
import { themeStyles, themeVars } from '../theme/theme';

export interface IFrontDrawer extends DrawerProps {
  header?: ReactNode;
  subheader?: ReactNode;
  footer?: ReactNode;
  width: DrawerWidth;
}

const FrontDrawerInternal = ({
  children,
  header,
  subheader,
  footer,
  width,
  ...rest
}: PropsWithChildren<IFrontDrawer>) => {
  const [headerRef, headerBounds] = useMeasure();
  const [footerRef, footerBounds] = useMeasure();

  return (
    <StyledDrawer {...rest} width={width}>
      <Container>
        <HeaderWrapper ref={headerRef}>
          <Title>
            {header}
            <FrontModalCloseButton onClick={(e) => rest.onClose?.(e, 'escapeKeyDown')} />
          </Title>
          {!!subheader && <Subtitle>{subheader}</Subtitle>}
        </HeaderWrapper>
        <Content excludedHeight={headerBounds.height + footerBounds.height}>{children}</Content>
        {!!footer && <Footer ref={footerRef}>{footer}</Footer>}
      </Container>
    </StyledDrawer>
  );
};

const StyledDrawer = styled((props: DrawerProps & Pick<IFrontDrawer, 'width'>) => (
  <Drawer {...props} />
))`
  & .${drawerClasses.modal} {
    color: ${(p) => p.theme.mainColors.titan900};
  }
  & .${drawerClasses.paper} {
    border-radius: 16px;
    margin: 16px;
    ${(p) =>
      p.anchor !== 'bottom' && p.anchor !== 'top'
        ? css`
            height: calc(100% - 32px);
            min-width: min(calc(100% - 32px), ${drawerWidths[p.width]});
            max-width: min(calc(100% - 32px), ${drawerWidths[p.width]});
          `
        : css`
            max-height: calc(100% - 32px);
          `}
  }
`;

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  max-height: 100%;
  position: relative;
`;

const HeaderWrapper = styled('div')`
  position: sticky;
  top: 0;
  padding: 16px 24px;
  background-color: ${themeVars.colors.white};
`;

const Title = styled(FrontModalHeader.Title)`
  display: flex;
  gap: 24px;
  justify-content: space-between;
`;

const Subtitle = styled(FrontModalHeader.Subtitle)`
  ${themeStyles.text.telka.body.lg}
  color: ${(p) => p.theme.mainColors.coldGrey600};
`;

const Content = styled('div')<{ excludedHeight: number }>`
  flex: 1;
  padding: 0 24px 16px;
  height: ${(p) => `calc(100% - ${32 + p.excludedHeight}px)`};
  overflow: scroll;
`;

const Footer = FrontModal.Footer;

export const FrontDrawer = Object.assign(FrontDrawerInternal, {
  Title,
  Subtitle,
  Content,
  Footer,
});
